import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, calcHours, fmtHours, formatCurrency, formatDate } from '../styles';
import { fetchLabourRates } from '../labourRates';
import { sendEmail } from '../utils/emailService';
import { dayworkSubmissionEmail } from '../utils/emailTemplates';

const TRADES = ['Fixer', 'Taper', 'Plasterer', 'Labourer', 'Site Manager', 'Supervisor', 'Other'];

const START_TIMES = ['07:00', '07:30', '08:00', '08:30', '09:00'];
const FINISH_TIMES = ['15:30', '16:00', '16:30', '17:00', '17:30', '18:00'];

function newLabour() {
  return { id: Date.now() + Math.random(), operative_name: '', trade: 'Fixer', start_time: '', finish_time: '', activity_note: '', start_other: false, finish_other: false };
}

function TimeQuickEntry({ label, value, presets, isOther, onChange, onToggleOther }) {
  const hasCustom = value && !presets.includes(value);
  const otherHighlighted = isOther || hasCustom;
  return (
    <div style={S.formGroup}>
      <label style={{ ...S.label, fontSize: '13px' }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {presets.map(t => {
          const active = value === t && !isOther;
          return (
            <button
              key={t}
              type="button"
              onClick={() => { onChange(t); onToggleOther(false); }}
              style={{
                padding: '6px 11px', borderRadius: '20px', lineHeight: '1',
                border: `1.5px solid ${active ? C.accent : C.border}`,
                background: active ? C.accent : '#fff',
                color: active ? '#fff' : C.text,
                fontSize: '13px', fontWeight: active ? '600' : '400',
                cursor: 'pointer', transition: 'all 0.1s',
              }}
            >
              {t}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onToggleOther(!isOther)}
          style={{
            padding: '6px 11px', borderRadius: '20px', lineHeight: '1',
            border: `1.5px solid ${otherHighlighted ? C.accent : C.border}`,
            background: otherHighlighted ? C.accent : '#fff',
            color: otherHighlighted ? '#fff' : C.textMuted,
            fontSize: '13px', fontWeight: otherHighlighted ? '600' : '400',
            cursor: 'pointer', transition: 'all 0.1s',
          }}
        >
          {hasCustom && !isOther ? value : 'Other'}
        </button>
      </div>
      {isOther && (
        <input
          type="time"
          style={{ ...S.input, marginTop: '8px' }}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoFocus
        />
      )}
    </div>
  );
}

function newMaterial() {
  return { id: Date.now() + Math.random(), description: '', quantity: '', unit: '', price_per_unit: '' };
}

function newPlant() {
  return { id: Date.now() + Math.random(), description: '', quantity: '', unit: 'Day', rate: '' };
}

function matLineTotal(row) {
  return (parseFloat(row.quantity) || 0) * (parseFloat(row.price_per_unit) || 0);
}

function plantLineTotal(row) {
  return (parseFloat(row.quantity) || 0) * (parseFloat(row.rate) || 0);
}

function ConfirmSubmitModal({ onConfirm, onCancel, saving }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: C.text }}>Submit to Main Contractor?</h2>
        <p style={{ color: C.textMuted, fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px 0' }}>
          This will create a permanent record and send the daywork sheet to the MC QS portal for signing. You will not be able to edit it after submission.
        </p>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', fontSize: '13px', color: '#374151' }}>
          The MC QS assigned to this project will receive a secure email link to sign or dispute the daywork sheet.
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ ...S.btn, ...S.btnGhost }} disabled={saving}>Cancel</button>
          <button onClick={onConfirm} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Submitting...' : 'Confirm Submission'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DayworkForm({ navigate, params }) {
  const { profile } = useAuth();
  const editId = params?.dayworkId || null;

  const [projects, setProjects] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [loading, setLoading] = useState(!!editId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [rates, setRates] = useState(null);
  const [lockedInstruction, setLockedInstruction] = useState(null);
  const [editRef, setEditRef] = useState(null);

  const [projectId, setProjectId] = useState(params?.projectId || '');
  const [instructionId, setInstructionId] = useState(params?.instructionId || '');
  const [dateOfWorks, setDateOfWorks] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [gridlines, setGridlines] = useState('');
  const [level, setLevel] = useState('');
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const [instructionAuthority, setInstructionAuthority] = useState(null);
  const [worksDescription, setWorksDescription] = useState('');
  const [reasonOutsideScope, setReasonOutsideScope] = useState('');

  // Continuation sheet tracking
  const continuationOf = params?.continuationOf || null;
  const continuationRef = params?.continuationRef || null;

  const [labour, setLabour] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plant, setPlant] = useState([]);

  useEffect(() => {
    if (editId) {
      loadEditData(editId);
      return;
    }
    if (!params?.instructionId && !continuationOf) {
      navigate('projects', {});
      return;
    }
    loadProjects();
    if (params?.instructionId) {
      supabase
        .from('instructions')
        .select('id, reference, type, description, location, gridlines, level, date, time, instructed_by_name, instructed_by_role, instruction_method')
        .eq('id', params.instructionId)
        .single()
        .then(({ data }) => { if (data) applyAutoFill(data); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (projectId) {
      loadInstructions(projectId);
      fetchLabourRates(projectId).then(setRates).catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  function applyAutoFill(ins) {
    const filled = new Set();
    if (ins.location) { setLocation(ins.location); filled.add('location'); }
    if (ins.gridlines) { setGridlines(ins.gridlines); filled.add('gridlines'); }
    if (ins.level) { setLevel(ins.level); filled.add('level'); }
    setInstructionAuthority({
      reference: ins.reference || null,
      type: ins.type || null,
      date: ins.date || null,
      time: ins.time || null,
      instructed_by_name: ins.instructed_by_name || null,
      instructed_by_role: ins.instructed_by_role || null,
      instruction_method: ins.instruction_method || null,
      description: ins.description || null,
    });
    setAutoFilledFields(filled);
  }

  async function loadProjects() {
    try {
      let query = supabase.from('projects').select('id, name, reference, project_code, daywork_seq_start, main_contractor').eq('active', true).order('name');
      if (profile.role !== 'admin') {
        const { data: pu } = await supabase.from('project_users').select('project_id').eq('user_id', profile.id);
        const ids = (pu || []).map(x => x.project_id);
        if (ids.length > 0) query = query.in('id', ids);
        else { setProjects([]); return; }
      }
      const { data, error: err } = await query;
      if (err) throw err;
      setProjects(data || []);
    } catch (err) {
      console.error('loadProjects error:', err);
    }
  }

  async function loadEditData(id) {
    setLoading(true);
    setError('');
    try {
      const [{ data: dw, error: dwErr }, { data: lab }, { data: mat }, { data: pla }] = await Promise.all([
        supabase.from('dayworks')
          .select('*, instructions(id, reference, type)')
          .eq('id', id)
          .single(),
        supabase.from('daywork_labour').select('*').eq('daywork_id', id).order('id'),
        supabase.from('daywork_materials').select('*').eq('daywork_id', id).order('id'),
        supabase.from('daywork_plant').select('*').eq('daywork_id', id).order('id'),
      ]);
      if (dwErr) throw dwErr;
      if (!dw) throw new Error('Daywork not found');
      if (dw.status !== 'draft') {
        navigate('daywork-detail', { dayworkId: id });
        return;
      }

      setProjectId(dw.project_id || '');
      setInstructionId(dw.instruction_id || '');
      setDateOfWorks(dw.date_of_works || new Date().toISOString().split('T')[0]);
      setDescription(dw.description || '');
      setLocation(dw.location || '');
      setGridlines(dw.gridlines || '');
      setLevel(dw.level || '');
      if (dw.instructions) setLockedInstruction({ reference: dw.instructions.reference, type: dw.instructions.type });
      setEditRef(dw.reference || null);
      try { setInstructionAuthority(dw.mc_instruction_authority ? JSON.parse(dw.mc_instruction_authority) : null); } catch (_) { setInstructionAuthority(null); }
      setWorksDescription(dw.works_description || '');
      setReasonOutsideScope(dw.reason_outside_scope || '');

      setLabour((lab || []).map(r => ({
        id: r.id,
        operative_name: r.operative_name || '',
        trade: r.trade || 'Fixer',
        start_time: r.start_time ? String(r.start_time).substring(0, 5) : '',
        finish_time: r.finish_time ? String(r.finish_time).substring(0, 5) : '',
        activity_note: r.activity_note || '',
        start_other: r.start_time ? !START_TIMES.includes(String(r.start_time).substring(0, 5)) : false,
        finish_other: r.finish_time ? !FINISH_TIMES.includes(String(r.finish_time).substring(0, 5)) : false,
      })));
      setMaterials((mat || []).map(r => ({
        id: r.id,
        description: r.description || '',
        quantity: String(r.quantity ?? ''),
        unit: r.unit || '',
        price_per_unit: String(r.price_per_unit ?? ''),
      })));
      setPlant((pla || []).map(r => ({
        id: r.id,
        description: r.description || '',
        quantity: String(r.quantity ?? ''),
        unit: r.unit || 'Day',
        rate: String(r.rate ?? ''),
      })));

      await loadProjects();
    } catch (err) {
      setError(err.message || 'Failed to load daywork for editing');
    } finally {
      setLoading(false);
    }
  }

  async function loadInstructions(pid) {
    setLoadingInstructions(true);
    try {
      const { data, error: err } = await supabase
        .from('instructions')
        .select('id, reference, type, description, status, location, gridlines, level, date, time, instructed_by_name, instructed_by_role, instruction_method')
        .eq('project_id', pid)
        .in('status', ['open', 'priced', 'daywork'])
        .order('created_at', { ascending: false });
      if (err) throw err;
      setInstructions(data || []);
    } catch (err) {
      console.error('loadInstructions error:', err);
    } finally {
      setLoadingInstructions(false);
    }
  }

  async function generateReference(pid) {
    const { data } = await supabase.from('dayworks').select('reference').eq('project_id', pid);
    const proj = projects.find(p => p.id === pid);
    const seqStart = proj?.daywork_seq_start || 1;
    const nums = (data || []).map(r => {
      const m = r.reference?.match(/(\d+)$/);
      return m ? parseInt(m[1], 10) : 0;
    });
    const next = nums.length > 0 ? Math.max(...nums) + 1 : seqStart;
    const code = proj?.project_code || proj?.reference || 'PRJ';
    return `DW-${code}-${String(next).padStart(4, '0')}`;
  }

  function buildChildRows(dayworkId) {
    const labourRows = labour.map(r => ({
      daywork_id: dayworkId,
      operative_name: r.operative_name || 'Unknown',
      trade: r.trade,
      start_time: r.start_time || null,
      finish_time: r.finish_time || null,
      hours_calculated: calcHours(r.start_time, r.finish_time) || null,
      activity_note: r.activity_note || null,
    }));
    const matRows = materials
      .filter(r => r.description?.trim())
      .map(r => ({
        daywork_id: dayworkId,
        description: r.description,
        quantity: parseFloat(r.quantity) || 0,
        unit: r.unit || null,
        price_per_unit: parseFloat(r.price_per_unit) || 0,
      }));
    const plantRows = plant
      .filter(r => r.description?.trim())
      .map(r => ({
        daywork_id: dayworkId,
        description: r.description,
        quantity: parseFloat(r.quantity) || 0,
        unit: r.unit || 'Day',
        rate: parseFloat(r.rate) || 0,
      }));
    return { labourRows, matRows, plantRows };
  }

  async function insertChildRows(dayworkId) {
    const { labourRows, matRows, plantRows } = buildChildRows(dayworkId);
    if (labourRows.length > 0) {
      const { error: lErr } = await supabase.from('daywork_labour').insert(labourRows);
      if (lErr) {
        console.error('Labour insert error:', lErr);
        setError(`Saved but labour records failed: ${lErr.message}. Please add them manually.`);
      }
    }
    if (matRows.length > 0) {
      const { error: mErr } = await supabase.from('daywork_materials').insert(matRows);
      if (mErr) console.error('Materials insert error:', mErr);
    }
    if (plantRows.length > 0) {
      const { error: pErr } = await supabase.from('daywork_plant').insert(plantRows);
      if (pErr) console.error('Plant insert error:', pErr);
    }
  }

  async function doPortalSubmit(dayworkId, pid) {
    try {
      // Step 1: look up MC QS via project_users join
      const { data: puData } = await supabase
        .from('project_users')
        .select('user_id, profiles(id, full_name, email, company, role, approved)')
        .eq('project_id', pid);

      let mcQsProfile = (puData || [])
        .map(u => u.profiles)
        .find(p => p && p.role === 'mc_qs' && p.approved === true);

      // Step 2: fall back to requested_project_id on profiles
      if (!mcQsProfile) {
        const { data: mcFromProfile } = await supabase
          .from('profiles')
          .select('id, full_name, email, company')
          .eq('role', 'mc_qs')
          .eq('approved', true)
          .eq('requested_project_id', pid);
        mcQsProfile = mcFromProfile?.[0] || null;
      }

      if (!mcQsProfile) {
        console.warn('No MC QS user found for project, skipping portal submission');
        return;
      }

      const token = crypto.randomUUID();
      const now = new Date().toISOString();
      const portalUrl = `https://proline.project-management.uk/mc-portal/daywork/${dayworkId}?token=${token}`;

      // Notify emails: Proline QS / admin on this project
      const notifyEmails = (puData || [])
        .map(u => u.profiles)
        .filter(p => p && (p.role === 'proline_qs' || p.role === 'admin'))
        .map(p => p.email)
        .filter(Boolean);

      await supabase.from('dayworks').update({
        submitted_to_name: mcQsProfile.full_name,
        submitted_to_email: mcQsProfile.email,
        submitted_to_company: mcQsProfile.company || null,
      }).eq('id', dayworkId);

      await supabase.from('mc_invitations').insert({
        project_id: pid,
        email: mcQsProfile.email,
        full_name: mcQsProfile.full_name,
        company: mcQsProfile.company || null,
        token, type: 'daywork', status: 'pending',
        created_by: profile.id,
        item_id: dayworkId, item_type: 'daywork',
        notify_emails: notifyEmails,
      });

      const { data: dwData } = await supabase
        .from('dayworks')
        .select('*, projects(id, name, reference), instructions(id, reference, type)')
        .eq('id', dayworkId)
        .single();

      if (dwData) {
        const { subject, htmlBody } = dayworkSubmissionEmail(
          dwData, dwData.projects, dwData.instructions, mcQsProfile.full_name, portalUrl
        );
        await sendEmail({ to: mcQsProfile.email, subject, htmlBody });
        await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });
      }

      await supabase.from('submission_log').insert({
        record_type: 'daywork', record_id: dayworkId,
        sent_to_name: mcQsProfile.full_name, sent_to_email: mcQsProfile.email,
        sent_to_company: mcQsProfile.company || null,
        sent_by: profile.id, token, is_resend: false,
      });
    } catch (err) {
      console.error('Portal submission error:', err);
    }
  }

  async function doSave(status) {
    setError('');
    setSaving(true);
    try {
      const now = new Date().toISOString();

      if (editId) {
        const editProj = projects.find(p => p.id === projectId);
        const editAuthorityJson = instructionAuthority ? JSON.stringify({ ...instructionAuthority, main_contractor: editProj?.main_contractor || null }) : null;
        const updatePayload = {
          date_of_works: dateOfWorks,
          description: description || null,
          location: location || null,
          gridlines: gridlines || null,
          level: level || null,
          mc_instruction_authority: editAuthorityJson,
          works_description: worksDescription || null,
          reason_outside_scope: reasonOutsideScope || null,
          status,
          submitted_at: status === 'submitted' ? now : null,
          submitted_by_name: status === 'submitted' ? (profile.full_name || null) : null,
        };
        const { error: updateErr } = await supabase.from('dayworks').update(updatePayload).eq('id', editId);
        if (updateErr) throw updateErr;

        await Promise.all([
          supabase.from('daywork_labour').delete().eq('daywork_id', editId),
          supabase.from('daywork_materials').delete().eq('daywork_id', editId),
          supabase.from('daywork_plant').delete().eq('daywork_id', editId),
        ]);
        await insertChildRows(editId);

        if (status === 'submitted') await doPortalSubmit(editId, projectId);

        navigate('daywork-detail', { dayworkId: editId });
        return;
      }

      const reference = await generateReference(projectId);
      const createProj = projects.find(p => p.id === projectId);
      const createAuthorityJson = instructionAuthority ? JSON.stringify({ ...instructionAuthority, main_contractor: createProj?.main_contractor || null }) : null;
      const payload = {
        project_id: projectId,
        instruction_id: instructionId,
        created_by: profile.id,
        reference,
        date_of_works: dateOfWorks,
        description: description || null,
        location: location || null,
        gridlines: gridlines || null,
        level: level || null,
        mc_instruction_authority: createAuthorityJson,
        works_description: worksDescription || null,
        reason_outside_scope: reasonOutsideScope || null,
        status,
        submitted_at: status === 'submitted' ? now : null,
        submitted_by_name: status === 'submitted' ? (profile.full_name || null) : null,
        continuation_of: continuationOf || null,
      };

      const { data: dw, error: dwErr } = await supabase
        .from('dayworks')
        .insert(payload)
        .select()
        .single();

      if (dwErr) {
        console.error('Daywork insert error:', dwErr);
        throw new Error(dwErr.message || dwErr.details || 'Database error saving daywork');
      }
      if (!dw) throw new Error('No data returned from insert — please try again');

      await insertChildRows(dw.id);

      if (status === 'submitted') await doPortalSubmit(dw.id, projectId);

      navigate('daywork-detail', { dayworkId: dw.id });
    } catch (err) {
      console.error('handleSave caught error:', err);
      setError(err.message || 'Failed to save daywork. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleSaveDraft() {
    if (!projectId) { setError('Please select a project.'); return; }
    if (!instructionId) { setError('Please select an instruction.'); return; }
    if (!dateOfWorks) { setError('Date of Works is required.'); return; }
    doSave('draft');
  }

  function handleSubmitClick() {
    if (!projectId) { setError('Please select a project.'); return; }
    if (!instructionId) { setError('Please select an instruction.'); return; }
    if (!dateOfWorks) { setError('Date of Works is required.'); return; }
    if (!worksDescription.trim()) { setError('Description of Works to be Carried Out is required.'); return; }
    if (!reasonOutsideScope.trim()) { setError('Reason Works Are Outside Subcontract Scope is required.'); return; }
    setShowSubmitModal(true);
  }

  function handleSubmitConfirm() {
    doSave('submitted');
  }

  const totalLabourHours = labour.reduce((sum, r) => sum + (calcHours(r.start_time, r.finish_time) || 0), 0);
  const totalMaterials = materials.reduce((sum, r) => sum + matLineTotal(r), 0);
  const totalPlant = plant.reduce((sum, r) => sum + plantLineTotal(r), 0);

  const labourCost = rates
    ? labour.reduce((sum, r) => sum + (calcHours(r.start_time, r.finish_time) || 0) * rates.getRateForTrade(r.trade), 0)
    : 0;
  const materialsWithUplift = rates ? totalMaterials * (1 + rates.materialsUplift / 100) : totalMaterials;
  const plantWithUplift = rates ? totalPlant * (1 + rates.plantUplift / 100) : totalPlant;
  const grandTotal = labourCost + materialsWithUplift + plantWithUplift;

  if (loading) return <div style={S.spinner}>Loading daywork...</div>;

  return (
    <div style={S.pageContainer}>
      {showSubmitModal && (
        <ConfirmSubmitModal
          onConfirm={handleSubmitConfirm}
          onCancel={() => setShowSubmitModal(false)}
          saving={saving}
        />
      )}

      <div style={S.pageHeader}>
        <div>
          <button
            onClick={() => editId ? navigate('daywork-detail', { dayworkId: editId }) : projectId ? navigate('project-detail', { projectId }) : navigate('projects', {})}
            style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '8px' }}
          >
            ← Back
          </button>
          <h1 style={S.pageTitle}>
            {editId ? 'Edit Daywork Sheet' : continuationOf ? 'Continuation Daywork Sheet' : 'New Daywork Sheet'}
          </h1>
          {continuationRef && (
            <p style={{ color: C.textMuted, fontSize: '14px', margin: '4px 0 0 0' }}>
              Continuation of <span style={{ fontFamily: 'monospace', fontWeight: '700', color: C.accent }}>{continuationRef}</span>
            </p>
          )}
          {!editId && !continuationOf && (
            <p style={{ color: C.textMuted, fontSize: '14px', margin: '4px 0 0 0' }}>
              Record labour, materials and plant for a single day
            </p>
          )}
        </div>
      </div>

      {error && <div style={{ ...S.alertError, marginBottom: '16px' }}>{error}</div>}

      {/* Section 1: Project & Instruction */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px' }}>1. Project & Instruction Link</h2>

        <div style={S.formGroup}>
          <label style={S.label}>Project <span style={{ color: C.danger }}>*</span></label>
          <select
            value={projectId}
            onChange={e => { setProjectId(e.target.value); setInstructionId(''); setAutoFilledFields(new Set()); }}
            style={{ ...S.select, width: '100%' }}
            disabled={!!continuationOf || !!editId}
          >
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.name}</option>)}
          </select>
        </div>

        {projectId && (
          <div style={{ marginTop: '12px' }}>
            <label style={{ ...S.label, marginBottom: '8px' }}>
              Instruction <span style={{ color: C.danger }}>*</span>
              {editId && (
                <span style={{ fontSize: '12px', fontWeight: '400', color: C.textMuted, marginLeft: '8px' }}>
                  locked — cannot change on an existing daywork
                </span>
              )}
              {!editId && (
                <span style={{ fontSize: '12px', fontWeight: '400', color: C.textMuted, marginLeft: '8px' }}>
                  Dayworks must be linked to an instruction
                </span>
              )}
            </label>
            {editId ? (
              lockedInstruction ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 14px', background: '#f1f5f9',
                  border: `1px solid ${C.border}`, borderRadius: '6px',
                }}>
                  <span style={{ fontWeight: '700', color: C.accent, fontFamily: 'monospace', fontSize: '15px' }}>
                    {lockedInstruction.reference}
                  </span>
                  {(() => {
                    const typeColors = { EWN: ['#dbeafe','#1e40af'], CVI: ['#fce7f3','#9d174d'], IR: ['#d1fae5','#065f46'] };
                    const [tbg, tc] = typeColors[lockedInstruction.type] || ['#f3f4f6','#374151'];
                    return (
                      <span style={{ background: tbg, color: tc, padding: '1px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: '700' }}>
                        {lockedInstruction.type}
                      </span>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ color: C.textMuted, fontSize: '14px' }}>Loading...</div>
              )
            ) : loadingInstructions ? (
              <div style={{ color: C.textMuted, fontSize: '14px' }}>Loading instructions...</div>
            ) : instructions.length === 0 ? (
              <div style={S.alertWarning}>
                No open instructions found for this project. Please{' '}
                <button
                  onClick={() => navigate('instruction-new', { projectId })}
                  style={{ background: 'none', border: 'none', color: C.warning, cursor: 'pointer', fontWeight: '600', padding: 0, textDecoration: 'underline' }}
                >
                  raise an instruction
                </button>
                {' '}first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {instructions.map(ins => {
                  const typeColors = { EWN: ['#dbeafe','#1e40af'], CVI: ['#fce7f3','#9d174d'], IR: ['#d1fae5','#065f46'] };
                  const [tbg, tc] = typeColors[ins.type] || ['#f3f4f6','#374151'];
                  return (
                    <div
                      key={ins.id}
                      onClick={() => { setInstructionId(ins.id); applyAutoFill(ins); }}
                      style={{
                        padding: '12px 14px',
                        border: `2px solid ${instructionId === ins.id ? C.accent : C.border}`,
                        borderRadius: '6px', cursor: 'pointer',
                        background: instructionId === ins.id ? '#f5f5f5' : '#fff',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '700', color: instructionId === ins.id ? C.accent : C.text }}>
                            {ins.reference}
                          </span>
                          <span style={{ background: tbg, color: tc, padding: '1px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: '700' }}>
                            {ins.type}
                          </span>
                        </div>
                        <span style={{ ...S.badge, background: '#f3f4f6', color: C.textMuted }}>{ins.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 2: Day Details */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px' }}>2. Day Details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Date of Works <span style={{ color: C.danger }}>*</span></label>
            <input type="date" style={S.input} value={dateOfWorks} onChange={e => setDateOfWorks(e.target.value)} />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Location</label>
            <input
              style={{ ...S.input, borderColor: autoFilledFields.has('location') ? '#86efac' : undefined }}
              value={location}
              onChange={e => { setLocation(e.target.value); setAutoFilledFields(s => { const n = new Set(s); n.delete('location'); return n; }); }}
              placeholder="Where were the works carried out?"
            />
            {autoFilledFields.has('location') && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '3px' }}>Copied from instruction — edit if different</div>}
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Gridlines</label>
            <input
              style={{ ...S.input, borderColor: autoFilledFields.has('gridlines') ? '#86efac' : undefined }}
              value={gridlines}
              onChange={e => { setGridlines(e.target.value); setAutoFilledFields(s => { const n = new Set(s); n.delete('gridlines'); return n; }); }}
              placeholder="e.g. A1–C3"
            />
            {autoFilledFields.has('gridlines') && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '3px' }}>Copied from instruction — edit if different</div>}
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Level / Floor</label>
            <input
              style={{ ...S.input, borderColor: autoFilledFields.has('level') ? '#86efac' : undefined }}
              value={level}
              onChange={e => { setLevel(e.target.value); setAutoFilledFields(s => { const n = new Set(s); n.delete('level'); return n; }); }}
              placeholder="e.g. Ground Floor"
            />
            {autoFilledFields.has('level') && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '3px' }}>Copied from instruction — edit if different</div>}
          </div>
        </div>
      </div>

      {/* Section 3: Authority for These Works */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '12px', fontSize: '15px' }}>3. Authority for These Works</h2>
        {instructionAuthority ? (
          <div style={{ background: '#f8fafc', border: '1px solid #d1d5db', borderRadius: '6px', padding: '14px 16px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Instruction Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', fontSize: '13px' }}>
              {instructionAuthority.reference && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Reference:</span> <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{instructionAuthority.reference}</span></div>}
              {instructionAuthority.type && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Type:</span> {instructionAuthority.type}</div>}
              {instructionAuthority.date && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Date:</span> {formatDate(instructionAuthority.date)}</div>}
              {instructionAuthority.time && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Time:</span> {instructionAuthority.time}</div>}
              {instructionAuthority.instructed_by_name && (
                <div><span style={{ fontWeight: '600', color: C.textMuted }}>Instructed by:</span> {instructionAuthority.instructed_by_name}{instructionAuthority.instructed_by_role ? ` (${instructionAuthority.instructed_by_role})` : ''}</div>
              )}
              {instructionAuthority.instruction_method && <div><span style={{ fontWeight: '600', color: C.textMuted }}>Method:</span> {instructionAuthority.instruction_method}</div>}
              {(() => { const mc = projects.find(p => p.id === projectId)?.main_contractor; return mc ? <div><span style={{ fontWeight: '600', color: C.textMuted }}>Main Contractor:</span> {mc}</div> : null; })()}
            </div>
            {instructionAuthority.description && (
              <>
                <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                  Description of Works from Main Contractor Instruction:
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', fontSize: '14px', color: '#111', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                  {instructionAuthority.description}
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ padding: '16px', textAlign: 'center', color: C.textMuted, fontSize: '14px', background: '#f9fafb', borderRadius: '6px' }}>
            Select an instruction above to populate authority details.
          </div>
        )}
      </div>

      {/* Section 4: Description of Works */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '8px', fontSize: '15px' }}>4. Description of Works to be Carried Out</h2>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 12px 0' }}>Describe in detail the works carried out on this day.</p>
        <div style={S.formGroup}>
          <textarea
            style={{ ...S.textarea, minHeight: '100px' }}
            value={worksDescription}
            onChange={e => setWorksDescription(e.target.value)}
            placeholder="Describe the works carried out..."
          />
        </div>
      </div>

      {/* Section 5: Reason Outside Scope */}
      <div style={{ ...S.card, marginBottom: '20px', borderLeft: '4px solid #f59e0b' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '8px', fontSize: '15px' }}>5. Reason Works Are Outside Subcontract Scope</h2>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 12px 0' }}>Explain why these works fall outside the agreed subcontract scope.</p>
        <div style={S.formGroup}>
          <textarea
            style={{ ...S.textarea, minHeight: '100px' }}
            value={reasonOutsideScope}
            onChange={e => setReasonOutsideScope(e.target.value)}
            placeholder="State why these works are outside your subcontract scope..."
          />
        </div>
      </div>

      {/* Section 6: Labour */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <div style={{ ...S.sectionHeader, marginBottom: '16px' }}>
          <h2 style={{ ...S.sectionTitle, fontSize: '15px', margin: 0 }}>6. Labour</h2>
          <button onClick={() => setLabour(l => [...l, newLabour()])} style={{ ...S.btn, ...S.btnPrimary, ...S.btnSm }}>
            + Add Operative
          </button>
        </div>

        {labour.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: C.textMuted, fontSize: '14px', background: '#f9fafb', borderRadius: '6px' }}>
            No operatives added. Click "Add Operative" to record labour.
          </div>
        ) : (
          <>
            {labour.map((row, idx) => {
              const hours = calcHours(row.start_time, row.finish_time) || 0;
              return (
                <div key={row.id} style={{ border: `1px solid ${C.border}`, borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                  <div style={{
                    background: '#f1f5f9', padding: '10px 14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: `1px solid ${C.border}`,
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: C.text }}>
                      Operative {idx + 1}{row.operative_name ? ` — ${row.operative_name}` : ''}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {hours > 0 && (
                        <span style={{ fontSize: '13px', color: C.success, fontWeight: '600' }}>{fmtHours(hours)}h</span>
                      )}
                      {hours > 0 && rates && (
                        <span style={{ fontSize: '13px', color: C.accent, fontWeight: '600' }}>
                          = {formatCurrency(hours * rates.getRateForTrade(row.trade))}
                        </span>
                      )}
                      <button onClick={() => setLabour(l => l.filter(r => r.id !== row.id))} style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={S.formGroup}>
                        <label style={{ ...S.label, fontSize: '13px' }}>Operative Name</label>
                        <input
                          style={S.input}
                          value={row.operative_name}
                          onChange={e => setLabour(l => l.map(r => r.id === row.id ? { ...r, operative_name: e.target.value } : r))}
                          placeholder="Full name"
                        />
                      </div>
                      <div style={S.formGroup}>
                        <label style={{ ...S.label, fontSize: '13px' }}>Trade</label>
                        <select style={S.select} value={row.trade} onChange={e => setLabour(l => l.map(r => r.id === row.id ? { ...r, trade: e.target.value } : r))}>
                          {TRADES.map(t => <option key={t}>{t}</option>)}
                        </select>
                        {rates && (
                          <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                            Rate: <span style={{ fontWeight: '600', color: C.text }}>£{rates.getRateForTrade(row.trade).toFixed(2)}/hr</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <TimeQuickEntry
                      label="Start Time"
                      value={row.start_time}
                      presets={START_TIMES}
                      isOther={row.start_other}
                      onChange={v => setLabour(l => l.map(r => r.id === row.id ? { ...r, start_time: v } : r))}
                      onToggleOther={v => setLabour(l => l.map(r => r.id === row.id ? { ...r, start_other: v } : r))}
                    />
                    <TimeQuickEntry
                      label="Finish Time"
                      value={row.finish_time}
                      presets={FINISH_TIMES}
                      isOther={row.finish_other}
                      onChange={v => setLabour(l => l.map(r => r.id === row.id ? { ...r, finish_time: v } : r))}
                      onToggleOther={v => setLabour(l => l.map(r => r.id === row.id ? { ...r, finish_other: v } : r))}
                    />
                    <div style={S.formGroup}>
                      <label style={{ ...S.label, fontSize: '13px' }}>Activity</label>
                      <input
                        style={S.input}
                        value={row.activity_note}
                        onChange={e => setLabour(l => l.map(r => r.id === row.id ? { ...r, activity_note: e.target.value } : r))}
                        placeholder="What were they doing? e.g. Installing new grid Level 1"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 12px', background: '#f0fdf4', borderRadius: '6px',
              border: `1px solid ${C.successBorder}`, fontSize: '14px', fontWeight: '600', color: C.success,
            }}>
              <span>Total Labour: {fmtHours(totalLabourHours)}h</span>
              {rates && <span>{formatCurrency(labourCost)}</span>}
            </div>
          </>
        )}
      </div>

      {/* Section 7: Materials */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <div style={{ ...S.sectionHeader, marginBottom: '16px' }}>
          <h2 style={{ ...S.sectionTitle, fontSize: '15px', margin: 0 }}>7. Materials</h2>
          <button onClick={() => setMaterials(m => [...m, newMaterial()])} style={{ ...S.btn, ...S.btnPrimary, ...S.btnSm }}>
            + Add Material
          </button>
        </div>
        {materials.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: C.textMuted, fontSize: '14px', background: '#f9fafb', borderRadius: '6px' }}>
            No materials recorded.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...S.table, marginBottom: '8px' }}>
                <thead>
                  <tr>
                    <th style={S.th}>Description</th>
                    <th style={S.th}>Qty</th>
                    <th style={S.th}>Unit</th>
                    <th style={S.th}>Rate (£)</th>
                    <th style={S.th}>Total</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map(row => (
                    <tr key={row.id}>
                      <td style={S.td}><input style={{ ...S.input, minWidth: '160px' }} value={row.description} onChange={e => setMaterials(m => m.map(r => r.id === row.id ? { ...r, description: e.target.value } : r))} placeholder="Material description" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: '80px' }} type="number" step="0.01" value={row.quantity} onChange={e => setMaterials(m => m.map(r => r.id === row.id ? { ...r, quantity: e.target.value } : r))} /></td>
                      <td style={S.td}><input style={{ ...S.input, width: '70px' }} value={row.unit} onChange={e => setMaterials(m => m.map(r => r.id === row.id ? { ...r, unit: e.target.value } : r))} placeholder="nr/m²" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: '90px' }} type="number" step="0.01" value={row.price_per_unit} onChange={e => setMaterials(m => m.map(r => r.id === row.id ? { ...r, price_per_unit: e.target.value } : r))} /></td>
                      <td style={{ ...S.td, fontWeight: '600' }}>£{matLineTotal(row).toFixed(2)}</td>
                      <td style={S.td}><button onClick={() => setMaterials(m => m.filter(r => r.id !== row.id))} style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f0fdf4', borderRadius: '6px', border: `1px solid ${C.successBorder}`, fontSize: '14px', fontWeight: '600', color: C.success }}>
              <span>Total Materials (at cost): £{totalMaterials.toFixed(2)}</span>
              {rates && rates.materialsUplift > 0 && (
                <span>{formatCurrency(materialsWithUplift)} <span style={{ fontWeight: '400', fontSize: '12px' }}>inc. {rates.materialsUplift}% uplift</span></span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Section 8: Plant */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <div style={{ ...S.sectionHeader, marginBottom: '16px' }}>
          <h2 style={{ ...S.sectionTitle, fontSize: '15px', margin: 0 }}>8. Plant & Equipment</h2>
          <button onClick={() => setPlant(p => [...p, newPlant()])} style={{ ...S.btn, ...S.btnPrimary, ...S.btnSm }}>
            + Add Plant
          </button>
        </div>
        {plant.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: C.textMuted, fontSize: '14px', background: '#f9fafb', borderRadius: '6px' }}>
            No plant or equipment recorded.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...S.table, marginBottom: '8px' }}>
                <thead>
                  <tr>
                    <th style={S.th}>Description</th>
                    <th style={S.th}>Qty</th>
                    <th style={S.th}>Unit</th>
                    <th style={S.th}>Rate</th>
                    <th style={S.th}>Total</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {plant.map(row => (
                    <tr key={row.id}>
                      <td style={S.td}><input style={{ ...S.input, minWidth: '150px' }} value={row.description} onChange={e => setPlant(p => p.map(r => r.id === row.id ? { ...r, description: e.target.value } : r))} placeholder="Plant description" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: '70px' }} type="number" step="0.01" value={row.quantity} onChange={e => setPlant(p => p.map(r => r.id === row.id ? { ...r, quantity: e.target.value } : r))} /></td>
                      <td style={S.td}>
                        <select style={{ ...S.select, width: '80px' }} value={row.unit} onChange={e => setPlant(p => p.map(r => r.id === row.id ? { ...r, unit: e.target.value } : r))}>
                          <option>Day</option>
                          <option>Hours</option>
                        </select>
                      </td>
                      <td style={S.td}><input style={{ ...S.input, width: '90px' }} type="number" step="0.01" value={row.rate} onChange={e => setPlant(p => p.map(r => r.id === row.id ? { ...r, rate: e.target.value } : r))} /></td>
                      <td style={{ ...S.td, fontWeight: '600' }}>£{plantLineTotal(row).toFixed(2)}</td>
                      <td style={S.td}><button onClick={() => setPlant(p => p.filter(r => r.id !== row.id))} style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f0fdf4', borderRadius: '6px', border: `1px solid ${C.successBorder}`, fontSize: '14px', fontWeight: '600', color: C.success }}>
              <span>Total Plant (at cost): £{totalPlant.toFixed(2)}</span>
              {rates && rates.plantUplift > 0 && (
                <span>{formatCurrency(plantWithUplift)} <span style={{ fontWeight: '400', fontSize: '12px' }}>inc. {rates.plantUplift}% uplift</span></span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Grand Total */}
      {rates && (labour.length > 0 || materials.length > 0 || plant.length > 0) && (
        <div style={{ ...S.card, marginBottom: '20px', border: `2px solid ${C.accent}` }}>
          <h2 style={{ ...S.sectionTitle, fontSize: '15px', margin: '0 0 14px 0' }}>Cost Summary</h2>
          <div style={{ fontSize: '14px' }}>
            {labour.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMuted }}>Labour ({fmtHours(totalLabourHours)}h)</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(labourCost)}</span>
              </div>
            )}
            {materials.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMuted }}>Materials {rates.materialsUplift > 0 ? `(inc. ${rates.materialsUplift}% uplift)` : ''}</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(materialsWithUplift)}</span>
              </div>
            )}
            {plant.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.textMuted }}>Plant {rates.plantUplift > 0 ? `(inc. ${rates.plantUplift}% uplift)` : ''}</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(plantWithUplift)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0 0' }}>
              <span style={{ fontWeight: '700', fontSize: '16px', color: C.text }}>TOTAL</span>
              <span style={{ fontWeight: '700', fontSize: '20px', color: C.accent }}>{formatCurrency(grandTotal)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => editId ? navigate('daywork-detail', { dayworkId: editId }) : projectId ? navigate('project-detail', { projectId }) : navigate('projects', {})}
          style={{ ...S.btn, ...S.btnGhost }}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={saving}
          style={{ ...S.btn, ...S.btnOutline, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          type="button"
          onClick={handleSubmitClick}
          disabled={saving}
          style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, opacity: saving ? 0.7 : 1 }}
        >
          Submit to Main Contractor
        </button>
      </div>
    </div>
  );
}
